/**
 * Canonical Blockwork glyphs, resolved by governed semantic meaning.
 *
 * The geometry is the vendored, immutable release under ./vendor (see ./provenance.json).
 * The meanings are the framework-owned ledger in ./semantic.json. Both are compiled into
 * ./generated/glyph-data.ts by tooling/vendor-glyphs.ts, so rendering is fully bundled:
 * inline SVG in currentColor, no runtime asset fetch.
 *
 * Contract:
 * - Colour binds through semantic roles on the surrounding element; the glyph inherits it.
 * - Sizes are 16 (compact utility), 20 (navigation and labels) and 24 (framework objects).
 * - Hidden from assistive technology by default; pass accessibleName only for the rare
 *   icon-only exception, which sets role="img" with that name.
 * - A meaning without a vendored asset renders nothing, so the visible text label beside
 *   the glyph keeps carrying the meaning on its own.
 */

import type { ReactElement } from "react";
import { GLYPH_BY_MEANING, GLYPH_SHAPES, type CanonicalGlyphName, type GlyphMeaning } from "./generated/glyph-data";
import { renderGlyph, type GlyphRenderProps } from "./render";

export type { CanonicalGlyphName, GlyphMeaning };
export type { GlyphSize } from "./render";

export type GlyphProps = GlyphRenderProps<GlyphMeaning>;

/** True when the meaning resolves to a vendored canonical glyph. */
export function hasGlyph(meaning: string): meaning is GlyphMeaning {
  return Object.hasOwn(GLYPH_BY_MEANING, meaning);
}

export function Glyph({ meaning, size = 20, badge, accessibleName, className }: GlyphProps): ReactElement | null {
  return renderGlyph(
    { meaning, size, badge, accessibleName, className },
    GLYPH_BY_MEANING,
    GLYPH_SHAPES
  );
}
